import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, ChevronLeft, Lock } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { StatusDot } from "@/components/StatusDot";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { get, post } from "@/lib/api";
import { archiveLine, cancelText, centsEur, historyHref, pdfName, sendStuck, statusTone } from "@/lib/documents";
import { clock, date as deDate, today } from "@/lib/format";
import type { Document, DocumentDetail as Detail, DocumentPreview, Pipeline } from "@/lib/types";
import { cn } from "@/lib/utils";
import { ApprovalBanner } from "./ApprovalBanner";
import { CancelDialog } from "./CancelDialog";
import { ConvertDialog } from "./ConvertDialog";
import { PaymentDialog } from "./PaymentDialog";
import { ReversePaymentDialog } from "./ReversePaymentDialog";
import { SendDialog, type SendKind, type SentState } from "./SendDialog";
import { useRefreshDocument } from "./useDocument";

const PARTS_EXIST = "Es gibt schon Teilrechnungen – bitte Schlussrechnung wählen";

const isOpenInvoice = (d: Document) => d.type === "invoice" && (d.status === "open" || d.status === "partially_paid");

function Card({ title, id, children, className }: { title?: string; id: string; children: React.ReactNode; className?: string }) {
  return (
    <section aria-labelledby={title ? id : undefined} className={cn("flex flex-col gap-3 rounded-xl border bg-card p-4", className)}>
      {title && <h2 id={id} className="text-[0.9375rem] font-semibold">{title}</h2>}
      {children}
    </section>
  );
}

function Figure({ label, children, strong }: { label: string; children: React.ReactNode; strong?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("truncate text-sm tabular-nums", strong && "font-semibold")}>{children}</div>
    </div>
  );
}

/**
 * „Ist raus.“ after a send, with the one authored motion of the flow: the amount travels from „Entwürfe“ to „Offen“.
 * The distance is measured, so the chip starts under the Entwürfe figure; under reduced motion it simply sits there.
 */
function SentBanner({ doc, copyTo }: { doc: Document; copyTo: string | null }) {
  const pipeline = useQuery({ queryKey: ["documents", "pipeline"], queryFn: () => get<Pipeline>("/api/documents/pipeline") });
  const from = useRef<HTMLSpanElement>(null);
  const chip = useRef<HTMLSpanElement>(null);
  const [travel, setTravel] = useState<number | null>(null);
  const invoice = doc.type === "invoice";
  useLayoutEffect(() => {
    if (!pipeline.data || !from.current || !chip.current || travel !== null) return;
    setTravel(from.current.getBoundingClientRect().left - chip.current.getBoundingClientRect().left);
  }, [pipeline.data, travel]);

  const meta = [
    doc.sentAt ? `Versendet um ${clock(doc.sentAt)}` : null,
    copyTo ? `Kopie an ${copyTo}` : null,
    invoice && doc.dueDate ? `fällig am ${deDate(doc.dueDate)}` : null,
    doc.type === "offer" && doc.validUntil ? `gültig bis ${deDate(doc.validUntil)}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <section role="status" aria-live="polite" className="flex flex-wrap items-center gap-x-8 gap-y-4 rounded-xl border bg-card px-5 py-4.5">
      <div className="flex min-w-0 flex-[1_1_20rem] items-center gap-3.5">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check aria-hidden className="size-4.5" strokeWidth={2.6} />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-base font-semibold">Ist raus. {centsEur(doc.totalCents)} sind unterwegs zu dir.</span>
          <span className="text-[0.8125rem] text-muted-foreground">{meta}</span>
        </div>
      </div>
      {invoice && pipeline.data && (
        <div aria-label="Pipeline nach dem Versand" role="group" className="flex flex-none items-center gap-14 max-sm:gap-8">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">Entwürfe</span>
            <span ref={from} className="text-sm font-medium tabular-nums">{centsEur(pipeline.data.drafts.cents)}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">Offen</span>
            <span className="flex items-center gap-2">
              <span className="text-sm font-semibold tabular-nums">{centsEur(pipeline.data.open.cents)}</span>
              <span
                ref={chip}
                style={travel !== null ? ({ "--travel-x": `${travel}px` } as React.CSSProperties) : undefined}
                className={cn(
                  "rounded-full bg-hl px-2 py-0.5 text-xs font-medium whitespace-nowrap tabular-nums",
                  travel !== null && "motion-safe:animate-[doc-travel_400ms_cubic-bezier(0.16,1,0.3,1)_both]",
                )}
              >
                + {centsEur(doc.totalCents)}
              </span>
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

/** Page 1 of the stored PDF, as the customer got it. */
function StoredPage({ doc }: { doc: Document }) {
  const preview = useQuery({
    queryKey: ["preview", doc.id, doc.updatedAt],
    queryFn: () => post<DocumentPreview>(`/api/documents/${doc.id}/preview`),
    staleTime: Infinity,
  });
  const page = preview.data?.pages[0];
  const failed = preview.data?.error ?? (preview.isError ? preview.error.message : null);
  return (
    <div className="flex flex-col gap-2">
      {failed && (
        <p role="alert" className="text-[0.8125rem] text-destructive">
          Vorschau nicht verfügbar: {failed}{" "}
          <a href={`/api/documents/${doc.id}/pdf`} target="_blank" rel="noopener" className="whitespace-nowrap text-foreground underline underline-offset-4">PDF öffnen</a>
        </p>
      )}
      <a
        href={`/api/documents/${doc.id}/pdf`}
        target="_blank"
        rel="noopener"
        aria-label={`${pdfName(doc, doc.number)} öffnen`}
        className="relative block aspect-[595.28/841.89] w-[24.8125rem] max-w-full overflow-hidden rounded-[3px] bg-white shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_2px_6px_rgb(0_0_0/0.08),0_12px_24px_-12px_rgb(0_0_0/0.18)] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        {page ? (
          <img src={`data:image/png;base64,${page}`} alt={`${doc.number}, Seite 1`} className="absolute inset-0 size-full" />
        ) : (
          !failed && <div aria-hidden className="absolute inset-0 animate-pulse bg-muted motion-reduce:animate-none" />
        )}
      </a>
    </div>
  );
}

function PaymentCard({
  detail, flashFrom, onRemind, onReverse,
}: { detail: Detail; flashFrom: number | null; onRemind: () => void; onReverse: (paymentId: number) => void }) {
  const doc = detail.document;
  const open = doc.totalCents - doc.paidCents;
  const overdue = isOpenInvoice(doc) && doc.dueDate !== null && doc.dueDate < today();
  return (
    <Card title="Zahlung" id="h-zahlung">
      <div className="grid grid-cols-3 gap-3">
        <Figure label="Betrag" strong>{centsEur(doc.totalCents)}</Figure>
        <Figure label="Bezahlt">{centsEur(doc.paidCents)}</Figure>
        <Figure label="Fällig">{doc.dueDate ? deDate(doc.dueDate) : "–"}</Figure>
      </div>
      {detail.payments.length > 0 && (
        <ul aria-label="Zahlungen" className="-mx-2 flex flex-col">
          {detail.payments.map((p) => (
            <li
              key={p.id}
              className={cn(
                "flex items-center gap-3 rounded-md px-2 py-1.5 text-sm tabular-nums",
                flashFrom !== null && p.id > flashFrom && "motion-safe:animate-[doc-flash_1200ms_ease-out_both]",
              )}
            >
              <span className="w-22 shrink-0 text-muted-foreground">{deDate(p.paidOn)}</span>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{p.note ?? ""}</span>
              <span className="font-medium">{centsEur(p.amountCents)}</span>
              {doc.status !== "cancelled" && (
                <button
                  type="button"
                  aria-label={`Zahlung über ${centsEur(p.amountCents)} vom ${deDate(p.paidOn)} zurücknehmen`}
                  className="-my-1 shrink-0 rounded-sm px-1 text-[0.8125rem] text-muted-foreground outline-none hover:text-destructive focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:min-h-11"
                  onClick={() => onReverse(p.id)}
                >
                  Zurücknehmen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {isOpenInvoice(doc) && (
        <p className="text-[0.8125rem] text-pretty text-muted-foreground">
          Beim nächsten Kontoauszug sucht der Agent nach {centsEur(open)} und „{doc.number}“ im Verwendungszweck.
        </p>
      )}
      {overdue && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-[0.8125rem]">
            <StatusDot tone="open">Überfällig seit {deDate(doc.dueDate!)}</StatusDot>
            {doc.remindedAt && <span className="text-muted-foreground"> · erinnert am {deDate(doc.remindedAt)}</span>}
          </span>
          <Button variant="outline" size="sm" className="ml-auto pointer-coarse:h-11" onClick={onRemind}>Zahlungserinnerung …</Button>
        </div>
      )}
    </Card>
  );
}

function OfferCard({ detail }: { detail: Detail }) {
  const doc = detail.document;
  const billing = detail.billing;
  return (
    <Card title="Angebot" id="h-angebot">
      <div className="grid grid-cols-3 gap-3">
        <Figure label="Betrag" strong>{centsEur(doc.totalCents)}</Figure>
        <Figure label="Verrechnet">{billing ? centsEur(billing.billedCents) : "–"}</Figure>
        <Figure label="Gültig bis">{doc.validUntil ? deDate(doc.validUntil) : "–"}</Figure>
      </div>
      {billing && billing.invoices.length > 0 && (
        <ul aria-label="Rechnungen zu diesem Angebot" className="-mx-2 flex flex-col">
          {billing.invoices.map((i) => (
            <li key={i.id}>
              <Link
                to={`/rechnungen/${i.id}`}
                className="flex gap-3 rounded-md px-2 py-1.5 text-sm tabular-nums outline-none hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:min-h-11 pointer-coarse:items-center"
              >
                <span className="w-20 shrink-0 font-medium">{i.number ?? "Entwurf"}</span>
                <span className="w-22 shrink-0 text-muted-foreground">{i.date ? deDate(i.date) : ""}</span>
                <span className={cn("ml-auto", i.status === "cancelled" && "text-muted-foreground line-through")}>{centsEur(i.totalCents)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {billing && <p className="text-[0.8125rem] text-muted-foreground">{billing.label}</p>}
    </Card>
  );
}

function Timeline({ detail }: { detail: Detail }) {
  return (
    <Card title="Verlauf" id="h-verlauf">
      <ol className="flex flex-col">
        {detail.timeline.map((e, i) => (
          <li key={i} className="flex gap-3">
            <div aria-hidden className="flex w-3 flex-col items-center">
              <span className={cn("mt-1.5 size-2 shrink-0 rounded-full", e.done ? "bg-muted-foreground" : "bg-card ring-2 ring-warning ring-inset")} />
              {i < detail.timeline.length - 1 && <span className="min-h-4 w-px flex-1 bg-border" />}
            </div>
            <div className="flex min-w-0 flex-col gap-px pb-3">
              <span className="text-sm font-medium">{e.title}</span>
              {e.meta && <span className="text-[0.8125rem] text-pretty text-muted-foreground">{e.meta}</span>}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** Crash between claiming the send and storing its result: Florian checks Postmark, then frees it for a new send. */
function StuckSend({ doc }: { doc: Document }) {
  const refresh = useRefreshDocument(doc.id);
  const reset = useMutation({
    mutationFn: () => post<Detail>(`/api/documents/${doc.id}/reset-send`),
    onSuccess: () => toast.success("Versand zurückgesetzt – du kannst jetzt neu senden"),
    onSettled: refresh,
  });
  return (
    <section role="alert" className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-warning/60 bg-warning/10 px-4 py-3">
      <div className="flex min-w-0 flex-[1_1_20rem] flex-col gap-0.5">
        <span className="text-sm font-semibold">Versand hängt</span>
        <span className="text-[0.8125rem] text-pretty text-muted-foreground">
          Der Versand startete um {clock(doc.sentAt!)} am {deDate(doc.sentAt!)} und wurde nie abgeschlossen. Erst zurücksetzen, wenn im
          Postmark-Protokoll keine E-Mail an den Kunden steht – sonst bekommt er sie beim nächsten Senden doppelt.
        </span>
        {reset.isError && <span className="text-[0.8125rem] text-destructive">{reset.error.message}</span>}
      </div>
      <Button variant="outline" className="pointer-coarse:h-11" disabled={reset.isPending} onClick={() => reset.mutate()}>
        Versand hängt – zurücksetzen
      </Button>
    </section>
  );
}

function OfferActions({ detail, onConvert }: { detail: Detail; onConvert: () => void }) {
  const doc = detail.document;
  const navigate = useNavigate();
  const refresh = useRefreshDocument(doc.id);
  const status = useMutation({
    mutationFn: (s: "accepted" | "rejected") => post<Document>(`/api/documents/${doc.id}/status`, { status: s }),
    onSuccess: (d) => toast.success(`${d.number} ${d.statusLabel}`),
    onError: (e) => toast.error(e.message),
    onSettled: refresh,
  });
  const full = useMutation({
    mutationFn: () => post<Document>(`/api/documents/${doc.id}/convert`, { kind: "full" }),
    onError: (e) => toast.error(e.message),
    onSettled: refresh,
  });
  const parts = (detail.billing?.invoices ?? []).some((i) => i.status !== "cancelled");

  if (doc.status === "sent") {
    return (
      <>
        <Button variant="outline" className="pointer-coarse:h-11" disabled={status.isPending} onClick={() => status.mutate("accepted")}>Angenommen</Button>
        <Button variant="outline" className="pointer-coarse:h-11" disabled={status.isPending} onClick={() => status.mutate("rejected")}>Abgelehnt</Button>
      </>
    );
  }
  if (doc.status !== "accepted" && doc.status !== "partially_invoiced") return null;
  const convert = (
    <Button
      variant="outline"
      className="pointer-coarse:h-11 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
      aria-disabled={parts || undefined}
      disabled={full.isPending}
      onClick={(e) => {
        if (parts) e.preventDefault();
        else full.mutate(undefined, { onSuccess: (d) => navigate(`/rechnungen/${d.id}`) });
      }}
    >
      In Rechnung umwandeln
    </Button>
  );
  return (
    <>
      {parts ? (
        <Tooltip>
          <TooltipTrigger asChild>{convert}</TooltipTrigger>
          <TooltipContent side="bottom">{PARTS_EXIST}</TooltipContent>
        </Tooltip>
      ) : convert}
      <Button variant="outline" className="pointer-coarse:h-11" onClick={onConvert}>In Teilen verrechnen …</Button>
    </>
  );
}

/**
 * A finalized document: the stored PDF, payment, history and the way to cancel it. `flashFrom`: payments with a
 * higher id were just booked and flash once; `onBooked` is called with the newest id before a booking.
 */
export function DocumentDetail({ detail, flashFrom, onBooked }: { detail: Detail; flashFrom: number | null; onBooked: () => void }) {
  const doc = detail.document;
  const [sp, setSp] = useSearchParams();
  const location = useLocation();
  // `?gesendet=1` shows the banner once: taken into state on arrival (after the sent document is loaded), then removed
  // from the URL, so a reload neither replays it nor claims a copy it cannot know about.
  const [sent, setSent] = useState<SentState | null>(null);
  const arrived = sp.get("gesendet") === "1" && doc.sentTo !== null;
  useEffect(() => {
    if (!arrived) return;
    setSent({ copyTo: (location.state as SentState | null)?.copyTo ?? null });
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("gesendet");
      return next;
    }, { replace: true });
  }, [arrived, location.state, setSp]);
  const [dialog, setDialog] = useState<"payment" | "cancel" | "convert" | SendKind | null>(null);
  const [reversing, setReversing] = useState<number | null>(null);
  const isInvoice = doc.type === "invoice";
  const cancellable = isInvoice && doc.status !== "cancelled";
  // Finalized without e-mail: an invoice can still go out; an offer only while it is not yet decided.
  const unsent = doc.sentAt === null && (doc.type === "offer" ? doc.status === "sent" : doc.status !== "cancelled" && doc.type !== "credit_note");
  const label = doc.number ?? "Dokument";

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Link to="/rechnungen" className="inline-flex items-center gap-1 self-start rounded-sm text-[0.8125rem] text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:min-h-11">
            <ChevronLeft aria-hidden className="size-3.5" />Rechnungen
          </Link>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-2xl font-semibold tracking-[-0.015em]">{label} · {doc.clientName}</h1>
            <StatusDot tone={statusTone(doc.status)} className="text-[0.8125rem]">{doc.statusLabel}</StatusDot>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {doc.type === "offer" && <OfferActions detail={detail} onConvert={() => setDialog("convert")} />}
          {unsent && !sendStuck(doc) && (
            <Button variant="outline" className="pointer-coarse:h-11" onClick={() => setDialog("document")}>Per E-Mail senden …</Button>
          )}
          <Button asChild variant="outline" className="pointer-coarse:h-11">
            <a href={`/api/documents/${doc.id}/pdf`} download={pdfName(doc, doc.number)}>PDF herunterladen</a>
          </Button>
          {isOpenInvoice(doc) && (
            <Button variant="outline" className="pointer-coarse:h-11" onClick={() => setDialog("payment")}>Zahlung erfassen</Button>
          )}
        </div>
      </div>

      <ApprovalBanner approvals={detail.approvals} number={doc.number} />
      {sendStuck(doc) && <StuckSend doc={doc} />}
      {sent && <SentBanner doc={doc} copyTo={sent.copyTo} />}

      <div className="flex flex-wrap items-start gap-5">
        <div className="flex min-w-0 flex-[1_1_26.25rem] flex-col gap-4">
          {doc.date && (
            <p className="flex items-center gap-2.5 text-[0.8125rem] text-muted-foreground">
              <Lock aria-hidden className="size-3.5 shrink-0" />
              {archiveLine(doc.date)}
            </p>
          )}
          <StoredPage doc={doc} />
        </div>

        <aside aria-label="Zahlung, Verlauf und Korrektur" className="flex min-w-0 flex-[999_1_23.75rem] flex-col gap-4">
          {isInvoice && <PaymentCard detail={detail} flashFrom={flashFrom} onRemind={() => setDialog("reminder")} onReverse={setReversing} />}
          {doc.type === "offer" && <OfferCard detail={detail} />}
          <Timeline detail={detail} />
          {cancellable && (
            <Card id="h-storno" className="flex-row flex-wrap items-center">
              <div className="flex flex-[1_1_15rem] flex-col gap-1">
                <h2 id="h-storno" className="text-[0.9375rem] font-semibold">Fehler entdeckt?</h2>
                <span className="text-[0.8125rem] text-pretty text-muted-foreground">
                  {doc.paidCents > 0 ? "Bezahlte Rechnungen erst nach Rückbuchung der Zahlung stornieren." : cancelText(doc)}
                </span>
              </div>
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive pointer-coarse:h-11"
                disabled={doc.paidCents > 0}
                onClick={() => setDialog("cancel")}
              >
                Stornieren …
              </Button>
            </Card>
          )}
          {doc.cancelledById !== null && (
            <p className="text-[0.8125rem] text-muted-foreground">
              Storniert{doc.cancelledAt ? ` am ${deDate(doc.cancelledAt)}` : ""} ·{" "}
              <Link to={`/rechnungen/${doc.cancelledById}`} className="text-foreground underline underline-offset-4">Stornorechnung ansehen</Link>
            </p>
          )}
          {doc.cancelsId !== null && (
            <p className="text-[0.8125rem] text-muted-foreground">
              <Link to={`/rechnungen/${doc.cancelsId}`} className="text-foreground underline underline-offset-4">Stornierte Rechnung ansehen</Link>
            </p>
          )}
          <Link to={historyHref(doc.number)} className="self-start rounded-sm text-[0.8125rem] text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:min-h-11 pointer-coarse:content-center">
            Alle Änderungen an {label} im Verlauf →
          </Link>
        </aside>
      </div>

      {(dialog === "document" || dialog === "reminder") && <SendDialog detail={detail} kind={dialog} onClose={() => setDialog(null)} />}
      {dialog === "payment" && (
        <PaymentDialog
          detail={detail}
          onClose={() => setDialog(null)}
          onBooked={onBooked}
        />
      )}
      {dialog === "cancel" && <CancelDialog detail={detail} onClose={() => setDialog(null)} />}
      {reversing !== null && <ReversePaymentDialog detail={detail} paymentId={reversing} onClose={() => setReversing(null)} />}
      {dialog === "convert" && detail.billing && <ConvertDialog detail={detail} onClose={() => setDialog(null)} />}
    </div>
  );
}
